// V1.0.64: Voice Recording dashboard - tổng quan device-based
import { useQuery } from '@tanstack/react-query';
import { Card, Row, Col, Statistic, Tag, Spin, Empty, Tooltip } from 'antd';
import { useNavigate } from 'react-router-dom';
import { Mic, HardDrive, FileAudio, Building2, AlertTriangle, CheckCircle } from 'lucide-react';
import { voiceService } from '@/services/voiceService';
import dayjs from 'dayjs';

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

function formatDuration(sec: number): string {
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

function formatRelative(d?: string | null): string {
  if (!d) return 'Chưa có';
  const diffMs = Date.now() - new Date(d).getTime();
  const mins = Math.floor(diffMs / 60_000);
  if (mins < 1) return 'Vừa xong';
  if (mins < 60) return `${mins} phút trước`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} giờ trước`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days} ngày trước`;
  return dayjs(d).format('DD/MM/YYYY HH:mm');
}

function statusColor(status: string): string {
  switch (status) {
    case 'ACTIVE': return '#52c41a';
    case 'WARNING': return '#faad14';
    case 'OFFLINE': return '#ff4d4f';
    default: return '#8a95a5';
  }
}

function statusLabel(status: string): string {
  switch (status) {
    case 'ACTIVE': return 'Đang hoạt động';
    case 'WARNING': return 'Có cảnh báo';
    case 'OFFLINE': return 'Mất kết nối';
    default: return 'Không rõ';
  }
}

export default function VoiceDashboardPage() {
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({
    queryKey: ['voice-dashboard'],
    queryFn: () => voiceService.getDashboard(),
    refetchInterval: 30_000,
  });

  if (isLoading) return <div style={{ padding: 40, textAlign: 'center' }}><Spin /></div>;
  if (!data) return <Empty description="Không tải được dữ liệu" />;

  return (
    <div style={{ padding: 20 }}>
      <div style={{ marginBottom: 20 }}>
        <h2 style={{ margin: 0 }}>
          <Mic size={20} style={{ verticalAlign: -3, marginRight: 8, color: '#6699CC' }} />
          Tổng quan ghi âm
        </h2>
        <div style={{ color: '#7d8a98', fontSize: 13, marginTop: 4 }}>
          Cập nhật mỗi 30 giây · Tổng dung lượng đang lưu: <b>{formatBytes(data.totalSizeBytes)}</b>
        </div>
      </div>

      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Máy đang hoạt động"
              value={data.activeDevicesCount}
              suffix={`/ ${data.totalDevicesCount}`}
              valueStyle={{ color: '#52c41a' }}
              prefix={<CheckCircle size={20} />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Chunks hôm nay"
              value={data.todayChunksCount}
              prefix={<FileAudio size={20} style={{ color: '#1976D2' }} />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Dung lượng hôm nay"
              value={formatBytes(data.todayTotalSizeBytes)}
              prefix={<HardDrive size={20} style={{ color: '#fa8c16' }} />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Tổng dung lượng"
              value={formatBytes(data.totalSizeBytes)}
              prefix={<HardDrive size={20} style={{ color: '#722ed1' }} />}
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        <Col xs={24} md={12}>
          <Card
            title={<><CheckCircle size={16} style={{ verticalAlign: -3, marginRight: 6, color: '#52c41a' }} />
              Máy đang hoạt động ({data.activeDevices.length})</>}
            size="small"
          >
            {data.activeDevices.length === 0 && <Empty description="Chưa có máy nào hoạt động" />}
            {data.activeDevices.map(d => (
              <div
                key={d.deviceId}
                onClick={() => navigate(`/voice-records/device/${d.deviceCode}`)}
                style={{
                  padding: '10px 12px', borderBottom: '1px solid #f0f0f0',
                  cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: 10,
                }}
                onMouseEnter={e => e.currentTarget.style.background = '#f5f9fc'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                <div style={{
                  width: 8, height: 8, borderRadius: '50%',
                  background: statusColor(d.status), flexShrink: 0
                }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 500 }}>
                    <span style={{ color: '#1976D2', fontFamily: 'monospace' }}>{d.deviceCode}</span>
                    <span style={{ marginLeft: 8 }}>{d.deviceName}</span>
                  </div>
                  <div style={{ fontSize: 11, color: '#8a95a5', marginTop: 2 }}>
                    {d.location && <>📍 {d.location} · </>}
                    {d.departmentName && <><Building2 size={10} style={{ verticalAlign: -1 }} /> {d.departmentName} · </>}
                    {d.lastChunkAt && <>Chunk gần nhất: {formatRelative(d.lastChunkAt)}</>}
                  </div>
                </div>
              </div>
            ))}
          </Card>
        </Col>

        <Col xs={24} md={12}>
          <Card
            title={<><AlertTriangle size={16} style={{ verticalAlign: -3, marginRight: 6, color: '#fa8c16' }} />
              Máy mất kết nối / cảnh báo ({data.offlineDevices.length})</>}
            size="small"
          >
            {data.offlineDevices.length === 0 && <Empty description="Tất cả máy đều hoạt động bình thường" />}
            {data.offlineDevices.map(d => (
              <div
                key={d.deviceId}
                onClick={() => navigate(`/voice-records/device/${d.deviceCode}`)}
                style={{
                  padding: '10px 12px', borderBottom: '1px solid #f0f0f0',
                  cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: 10,
                }}
                onMouseEnter={e => e.currentTarget.style.background = '#fff7e6'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                <Tooltip title={statusLabel(d.status)}>
                  <div style={{
                    width: 8, height: 8, borderRadius: '50%',
                    background: statusColor(d.status), flexShrink: 0
                  }} />
                </Tooltip>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 500 }}>
                    <span style={{ color: '#1976D2', fontFamily: 'monospace' }}>{d.deviceCode}</span>
                    <span style={{ marginLeft: 8 }}>{d.deviceName}</span>
                    <Tag color={d.status === 'WARNING' ? 'orange' : 'red'} style={{ marginLeft: 8, fontSize: 10 }}>
                      {statusLabel(d.status)}
                    </Tag>
                  </div>
                  <div style={{ fontSize: 11, color: '#8a95a5', marginTop: 2 }}>
                    {d.lastChunkAt
                      ? <>Chunk gần nhất: {formatRelative(d.lastChunkAt)}</>
                      : 'Chưa có chunk nào'}
                  </div>
                </div>
              </div>
            ))}
          </Card>
        </Col>
      </Row>

      <Row style={{ marginTop: 16 }}>
        <Col span={24}>
          <Card title="Lượng ghi âm 7 ngày qua" size="small">
            {data.last7DaysVolume.length === 0
              ? <Empty description="Chưa có dữ liệu" />
              : (
                <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', height: 140, paddingBottom: 8 }}>
                  {data.last7DaysVolume.map(d => {
                    const maxSize = Math.max(...data.last7DaysVolume.map(x => x.totalSizeBytes), 1);
                    const heightPct = (d.totalSizeBytes / maxSize) * 100;
                    return (
                      <div key={d.date} style={{
                        flex: 1, display: 'flex', flexDirection: 'column',
                        alignItems: 'center', gap: 4, height: '100%', justifyContent: 'flex-end'
                      }}>
                        <div style={{ fontSize: 10, color: '#5b6875' }}>{d.chunkCount}</div>
                        <Tooltip title={`${d.chunkCount} chunks · ${formatBytes(d.totalSizeBytes)} · ${formatDuration(d.totalDurationSec)}`}>
                          <div style={{
                            width: '100%',
                            height: `${Math.max(2, heightPct)}%`,
                            minHeight: 2,
                            background: 'linear-gradient(180deg, #6699CC 0%, #4080BC 100%)',
                            borderRadius: '3px 3px 0 0',
                          }} />
                        </Tooltip>
                        <div style={{ fontSize: 10, color: '#8a95a5' }}>
                          {dayjs(d.date).format('DD/MM')}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
          </Card>
        </Col>
      </Row>
    </div>
  );
}
